import { useEffect, useState, Fragment, useCallback } from 'react';
import { getPublicCms } from '@/api/cms';
import { getPublicSolutions } from '@/api/solution';
import { getPublicTools } from '@/api/tool';

interface HeroStatConfig {
  id: string;
  target: number;
  suffix: string;
  label: string;
  sublabel: string;
  icon: string;
  color: string;
  autoMode?: boolean;
}

interface AboutContent {
  tag: string;
  title: string;
  body1: string;
  body2: string;
  body3: string;
}

const EMPTY_ABOUT: AboutContent = {
  tag: '',
  title: '',
  body1: '',
  body2: '',
  body3: '',
};

function readText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function readNumber(value: unknown, fallback = 0): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return fallback;
  return value;
}

function normalizeAboutContent(raw: unknown): AboutContent {
  if (!raw || typeof raw !== 'object') return EMPTY_ABOUT;
  const about = raw as Record<string, unknown>;
  return {
    tag: readText(about.tag),
    title: readText(about.title),
    body1: readText(about.body1),
    body2: readText(about.body2),
    body3: readText(about.body3),
  };
}

function normalizeHeroStats(raw: unknown): HeroStatConfig[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, idx): HeroStatConfig | null => {
      if (!item || typeof item !== 'object') return null;
      const stat = item as Record<string, unknown>;
      return {
        id: readText(stat.id) || `stat-${idx + 1}`,
        target: readNumber(stat.target, 0),
        suffix: readText(stat.suffix),
        label: readText(stat.label),
        sublabel: readText(stat.sublabel),
        icon: readText(stat.icon) || 'ri-bar-chart-box-line',
        color: readText(stat.color) || '#0ea5e9',
        autoMode: Boolean(stat.autoMode),
      } satisfies HeroStatConfig;
    })
    .filter((item): item is HeroStatConfig => Boolean(item));
}

export default function AboutSection() {
  const [content, setContent] = useState<AboutContent>(EMPTY_ABOUT);
  const [heroStats, setHeroStats] = useState<HeroStatConfig[]>([]);
  const [contentError, setContentError] = useState<string | null>(null);
  const [solutionsCount, setSolutionsCount] = useState(0);
  const [toolsCount, setToolsCount] = useState(0);

  const refreshContent = useCallback(async () => {
    try {
      const cms = await getPublicCms('home_page_content');
      setContent(normalizeAboutContent(cms?.about));
      setHeroStats(normalizeHeroStats(cms?.stats));
      setContentError(null);
    } catch (err) {
      setContent(EMPTY_ABOUT);
      setHeroStats([]);
      setContentError(err instanceof Error ? err.message : '首页关于区内容加载失败');
    }
  }, []);

  const refreshDynamicCounts = useCallback(async () => {
    try {
      const [solutionRes, toolRes] = await Promise.all([getPublicSolutions(), getPublicTools()]);
      const onlineSolutions = Array.isArray(solutionRes?.list)
        ? solutionRes.list.filter((item) => !item.offline).length
        : 0;
      const onlineTools = Array.isArray(toolRes?.list)
        ? toolRes.list.filter((item) => !item.offline).length
        : 0;
      setSolutionsCount(onlineSolutions);
      setToolsCount(onlineTools);
    } catch {
      setSolutionsCount(0);
      setToolsCount(0);
    }
  }, []);

  useEffect(() => {
    void refreshContent();
    void refreshDynamicCounts();

    const handleContentRefresh = () => void refreshContent();
    const handleDataRefresh = () => void refreshDynamicCounts();

    window.addEventListener('wanxiang_page_content_updated', handleContentRefresh);
    window.addEventListener('wanxiang_solutions_updated', handleDataRefresh);
    window.addEventListener('wanxiang_tools_updated', handleDataRefresh);

    return () => {
      window.removeEventListener('wanxiang_page_content_updated', handleContentRefresh);
      window.removeEventListener('wanxiang_solutions_updated', handleDataRefresh);
      window.removeEventListener('wanxiang_tools_updated', handleDataRefresh);
    };
  }, [refreshContent, refreshDynamicCounts]);

  const features = [
    {
      icon: 'ri-open-source-line',
      title: '完全开源',
      desc: '所有推荐方案均为开源软件，源代码公开透明，无供应商锁定风险。',
      color: '#0ea5e9',
    },
    {
      icon: 'ri-money-cny-circle-line',
      title: '极低成本',
      desc: '相比商业软件节省80%以上费用，自部署无需支付昂贵的年费和用户授权费。',
      color: '#f97316',
    },
    {
      icon: 'ri-shield-check-line',
      title: '数据自主',
      desc: '数据存储在自己的服务器，完全掌控数据安全，不依赖第三方云服务。',
      color: '#10b981',
    },
    {
      icon: 'ri-community-line',
      title: '社区支持',
      desc: '活跃的开源社区提供持续更新和技术支持，遇到问题不再孤立无援。',
      color: '#8b5cf6',
    },
  ];

  return (
    <section id="about" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#10b981]/10 text-[#10b981] text-sm font-medium mb-6">
              <i className="ri-information-line"></i>
              {content.tag || '无'}
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
              {content.title.includes('邪修') ? (
                (() => {
                  const parts = content.title.split('邪修');
                  return (
                    <>
                      {parts[0]}
                      <span className="text-[#0ea5e9]">邪修</span>
                      {parts[1]}
                    </>
                  );
                })()
              ) : content.title || '无'}
            </h2>
            <p className="text-gray-600 mb-4 leading-relaxed">{content.body1 || '无'}</p>
            <p className="text-gray-600 mb-4 leading-relaxed">{content.body2 || '无'}</p>
            <p className="text-gray-600 mb-8 leading-relaxed">{content.body3 || '无'}</p>

            {contentError ? (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-500">
                核心数据加载失败：{contentError}
              </div>
            ) : heroStats.length === 0 ? (
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-400">
                暂无核心数据
              </div>
            ) : (
              <div className="flex items-center gap-4 flex-wrap">
                {heroStats.map((stat, idx) => {
                  const displayVal = stat.autoMode && (stat.id === 'stat-solutions' || stat.id === 'stat-tools')
                    ? (stat.id === 'stat-solutions' ? solutionsCount : toolsCount)
                    : stat.target;
                  return (
                    <Fragment key={stat.id}>
                      {idx > 0 && <div className="w-px h-12 bg-gray-200 hidden sm:block"></div>}
                      <div className="text-center">
                        <div className="text-3xl font-bold" style={{ color: stat.color }}>
                          {displayVal}
                          {stat.suffix}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">{stat.label || '无'}</div>
                      </div>
                    </Fragment>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {features.map((f) => (
              <div key={f.title} className="p-5 bg-[#f8fafc] rounded-2xl border border-gray-100 hover:border-gray-200 transition-all">
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-xl mb-3"
                  style={{ backgroundColor: `${f.color}15` }}
                >
                  <i className={`${f.icon} text-lg`} style={{ color: f.color }}></i>
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-2">{f.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
